// Centralized, fail-soft Sentry wiring for the web app (sotravel_fe).
//
// `@sentry/react` is a dependency. The DSN is HARDCODED below (not read from a
// build-time env) because the CRA app is built/served as a static bundle and we
// have no operator-editable env on the host — same rationale as the backend's
// config/sentry.js. A Sentry DSN is an ingest key, not a secret: it only permits
// SENDING events to this project, so it is safe to commit. `REACT_APP_SENTRY_DSN`
// still overrides it when explicitly set (including "" to disable locally).
//
// Project: sotravel-new / sotravel-frontend.

import * as Sentry from "@sentry/react";

const DEFAULT_SENTRY_DSN =
  "https://fb24dd863bc324f59effdcc77ca78498@o4511211504402432.ingest.us.sentry.io/4511646674518016";

const SENTRY_DSN =
  process.env.REACT_APP_SENTRY_DSN !== undefined
    ? process.env.REACT_APP_SENTRY_DSN
    : DEFAULT_SENTRY_DSN;

// Treat the real customer domains as "production"; everything else (localhost,
// ngrok tunnels, previews) is "development" so prod dashboards stay clean.
function resolveEnvironment() {
  if (process.env.REACT_APP_SENTRY_ENV) return process.env.REACT_APP_SENTRY_ENV;
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (/(^|\.)sotravel\.com$/.test(host) || /(^|\.)sotravel\.me$/.test(host)) {
      return "production";
    }
  }
  return "development";
}

let initialized = false;

export function initFrontendSentry() {
  if (initialized || !SENTRY_DSN) return;
  try {
    const environment = resolveEnvironment();
    const isProd = environment === "production";
    Sentry.init({
      dsn: SENTRY_DSN,
      environment,
      // Tracing: sample a slice of transactions so the checkout/reserve API
      // latency that drives the relogin / saved-cards / 502 reports is
      // observable, without flooding quota.
      tracesSampleRate: isProd ? 0.1 : 1.0,
      // Propagate trace headers ONLY to our own API so a slow FE request can be
      // correlated with the matching BE transaction. The BE CORS allowlist must
      // permit `sentry-trace` + `baggage` (added in sotravel_be/app.js).
      tracePropagationTargets: [/api\.sotravel\.me/, /sotravel-be\.ngrok\.app/, "localhost"],
      // Session Replay — capture only on error, and HARD-mask everything. The
      // checkout page renders card inputs, so this is mandatory: no raw text, no
      // input values, no media ever leave the browser.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: isProd ? 0.2 : 0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      // Drop noisy, non-actionable errors that aren't ours.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
        "Non-Error promise rejection captured",
      ],
    });
    // Existing call sites (services/uploads.js) already reference
    // `window.Sentry?.captureException`; expose the SDK so those work too.
    if (typeof window !== "undefined") window.Sentry = Sentry;
    initialized = true;
  } catch (e) {
    // Never let monitoring wiring break app boot.
    // eslint-disable-next-line no-console
    console.warn("[sentry] frontend init skipped:", e?.message || e);
  }
}

// Tagged capture helper used across the auth + checkout resilience paths.
// `tags` show up as filterable facets in Sentry (e.g. surface, area, status).
export function captureFrontendException(err, { tags, extra, level } = {}) {
  try {
    if (!initialized) return;
    Sentry.captureException(err, { tags, extra, level });
  } catch (_) {}
}

export function identifyUser(user) {
  try {
    if (!initialized || !user?.id) return;
    Sentry.setUser({ id: user.id, email: user.email });
  } catch (_) {}
}

export function clearUser() {
  try {
    if (!initialized) return;
    Sentry.setUser(null);
  } catch (_) {}
}
